import api from './api';
import { normalizePlantilla } from '../data/mockPlantillasCiclicos';
import { normalizeCiclo, STORAGE_CICLOS_CICLICOS, loadCiclosCiclicos } from '../data/mockCiclosCiclicos';

const base = '/produccion-concesionaria';

function messageFromApiError(data) {
  if (!data) return 'Error de red o del servidor';
  if (data.errors && typeof data.errors === 'object') {
    return Object.values(data.errors)
      .flat()
      .filter(Boolean)
      .join(' ');
  }
  return data.message || 'Error al procesar la solicitud';
}

const emptyPlaneamientoCiclicos = () => ({
  proyectos: [],
  plantillas: [],
  ciclos: [],
  tiempos: [],
  tiposPlato: [],
  recetas: [],
});

/**
 * Proyectos + plantillas + cíclicos + tiempos/tipos de menú + recetas (platos) en una sola petición.
 */
export async function fetchDatosPlaneamientoCiclicos() {
  const res = await api.get(`${base}/planeamiento/datos-ciclicos`);
  if (!res.data?.success || !res.data?.data) {
    return emptyPlaneamientoCiclicos();
  }
  const d = res.data.data;
  return {
    proyectos: Array.isArray(d.proyectos) ? d.proyectos : [],
    plantillas: (d.plantillas || []).map((p) => normalizePlantilla(p)),
    ciclos: (d.ciclos || []).map((c) => normalizeCiclo(c)),
    tiempos: Array.isArray(d.tiempos) ? d.tiempos : [],
    tiposPlato: Array.isArray(d.tiposPlato) ? d.tiposPlato : [],
    recetas: Array.isArray(d.recetas) ? d.recetas : [],
  };
}

export async function fetchCiclicosList() {
  const res = await api.get(`${base}/ciclicos`);
  if (!res.data?.success || !Array.isArray(res.data.data)) {
    return [];
  }
  return res.data.data.map((c) => normalizeCiclo(c));
}

export async function fetchCiclico(id) {
  try {
    const res = await api.get(`${base}/ciclicos/${id}`);
    if (!res.data?.success || !res.data.data) {
      return null;
    }
    return normalizeCiclo(res.data.data);
  } catch {
    return null;
  }
}

/**
 * @param {{ nombre: string, proyectoId: string, plantillaId: string, anio: number, numeroSemana: number, fechaInicio: string, fechaFin: string, matrizPlatos?: object }} payload
 */
export async function createCiclico(payload) {
  try {
    const res = await api.post(`${base}/ciclicos`, payload);
    if (!res.data?.success || !res.data.data) {
      throw new Error(messageFromApiError(res.data));
    }
    return normalizeCiclo(res.data.data);
  } catch (e) {
    const data = e.response?.data;
    if (data) throw new Error(messageFromApiError(data));
    throw new Error(e.message || 'Error de conexión');
  }
}

/**
 * @param {string|number} id
 * @param {object} payload campos parciales (p. ej. { matrizPlatos })
 */
export async function updateCiclico(id, payload) {
  try {
    const res = await api.put(`${base}/ciclicos/${id}`, payload);
    if (!res.data?.success || !res.data.data) {
      throw new Error(messageFromApiError(res.data));
    }
    return normalizeCiclo(res.data.data);
  } catch (e) {
    const data = e.response?.data;
    if (data) throw new Error(messageFromApiError(data));
    throw new Error(e.message || 'Error de conexión');
  }
}

export async function deleteCiclico(id) {
  try {
    const res = await api.delete(`${base}/ciclicos/${id}`);
    if (!res.data?.success) {
      throw new Error(res.data?.message || messageFromApiError(res.data));
    }
  } catch (e) {
    if (e.response) {
      const data = e.response.data;
      throw new Error(data?.message || messageFromApiError(data) || 'Error al eliminar');
    }
    throw e;
  }
}

export async function confirmarSalidaProduccion(payload) {
  try {
    const res = await api.post(`${base}/operaciones/confirmar-salida-produccion`, payload);
    if (!res.data?.success) {
      throw new Error(messageFromApiError(res.data));
    }
    return res.data;
  } catch (e) {
    const data = e.response?.data;
    if (data) throw new Error(messageFromApiError(data));
    throw new Error(e.message || 'Error de conexión');
  }
}

export async function revertirSalidaProduccion(payload) {
  try {
    const res = await api.post(`${base}/operaciones/revertir-salida-produccion`, payload);
    if (!res.data?.success) {
      throw new Error(messageFromApiError(res.data));
    }
    return res.data;
  } catch (e) {
    const data = e.response?.data;
    if (data) throw new Error(messageFromApiError(data));
    throw new Error(e.message || 'Error de conexión');
  }
}

/**
 * Sube a servidor los cíclicos guardados solo en el navegador (transición) y limpia la clave local.
 * Ignora filas cuyo proyecto/plantilla no existan en el payload del servidor.
 */
export async function migrarCiclosLocalesAlServidor() {
  const local = loadCiclosCiclicos();
  if (!local.length) return { migrados: 0, omitidos: 0 };

  const bundle = await fetchDatosPlaneamientoCiclicos();
  const pids = new Set((bundle.proyectos || []).map((p) => String(p.id)));
  const tids = new Set((bundle.plantillas || []).map((p) => String(p.id)));

  let migrados = 0;
  let omitidos = 0;

  for (const c of local) {
    const pid = String(c.proyectoId || '');
    const tid = String(c.plantillaId || '');
    if (!pid || !tid || !pids.has(pid) || !tids.has(tid)) {
      omitidos += 1;
      continue;
    }
    if (String(c.id || '').startsWith('ciclo-demo-')) {
      omitidos += 1;
      continue;
    }
    try {
      await createCiclico({
        nombre: c.nombre,
        proyectoId: pid,
        plantillaId: tid,
        anio: c.anio,
        numeroSemana: c.numeroSemana,
        fechaInicio: c.fechaInicio,
        fechaFin: c.fechaFin,
        matrizPlatos: c.matrizPlatos && typeof c.matrizPlatos === 'object' ? c.matrizPlatos : undefined,
      });
      migrados += 1;
    } catch {
      omitidos += 1;
    }
  }

  if (migrados > 0) {
    try {
      localStorage.removeItem(STORAGE_CICLOS_CICLICOS);
    } catch {
      /* ignore */
    }
  }

  return { migrados, omitidos };
}
